import { useEffect, useState } from "react";
import { http, apiErr } from "@/lib/api";
import { Loading } from "@/components/common";
import { CheckCircle2, XCircle, Info } from "lucide-react";

const STAGE = {
  uso_recente: ["Uso recente", "bg-emerald-500/10 text-emerald-300 border-emerald-400/30"],
  primo_preventivo: ["Primo preventivo", "bg-sky-500/10 text-sky-300 border-sky-400/30"],
  configurazione_avviata: ["Configurazione avviata", "bg-violet-500/10 text-violet-300 border-violet-400/30"],
  solo_registrato: ["Solo registrato", "bg-slate-500/10 text-slate-300 border-slate-400/30"],
};
const NA = "Non disponibile";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : NA);
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : NA);
const Flag = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] ${ok ? "text-emerald-300" : "text-slate-500"}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{label}
  </span>
);

function Row({ t }) {
  const [label, cls] = STAGE[t.stage] || STAGE.solo_registrato;
  const sub = t.subscription || {};
  const subTxt = sub.plan ? `${sub.plan} / ${sub.interval || "—"}${sub.current_period_end ? ` · fino al ${day(sub.current_period_end)}` : ""}` : "—";
  return (
    <tr data-testid="usage-row" className={`border-t border-white/10 align-top ${t.is_test ? "opacity-60" : ""}`}>
      <td className="py-3 pr-3">
        <div className="font-semibold text-slate-100" data-testid="usage-name">{t.name} {t.is_test && <span className="ml-1 text-[9px] uppercase border border-white/20 rounded px-1 text-slate-400">test</span>}</div>
        <div className="text-[11px] text-slate-500">/{t.slug} · iscritta {day(t.created_at)} · {t.users} utenti</div>
        <div className="mt-1"><span data-testid="usage-stage" className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>{label}</span></div>
      </td>
      <td className="py-3 pr-3 text-xs">
        <div className="text-slate-300">{t.status}</div>
        {t.trial?.ends_at && t.status === "TRIAL" && <div className="text-slate-500">prova fino al {day(t.trial.ends_at)}</div>}
        <div className="text-slate-500">{subTxt}</div>
      </td>
      <td className="py-3 pr-3 text-xs">
        <div data-testid="usage-last-login" className={t.last_login_at ? "text-slate-200" : "text-slate-500 italic"}>{fmt(t.last_login_at)}</div>
      </td>
      <td className="py-3 pr-3 text-xs text-slate-200" data-testid="usage-counts">
        {t.counts.customers} clienti · {t.counts.projects} pratiche · {t.counts.quotes} preventivi
        <div className="text-slate-500">{t.counts.quotes_sent} inviati · listino {t.counts.price_rules} voci</div>
      </td>
      <td className="py-3 pr-3 text-xs" data-testid="usage-pdf">
        <span className="text-slate-200">{t.pdf.generated}</span>
        <div className="text-slate-500">{t.pdf.tracking_since ? `dal ${day(t.pdf.tracking_since)}` : "nessun evento tracciato"}</div>
      </td>
      <td className="py-3 pr-3 text-xs" data-testid="usage-last-activity">
        {t.last_activity ? <><div className="text-slate-200">{fmt(t.last_activity.at)}</div><div className="text-slate-500">fonte: {t.last_activity.source}</div></>
          : <span className="text-slate-500 italic">{NA}</span>}
      </td>
      <td className="py-3 text-xs flex flex-col gap-0.5" data-testid="usage-config">
        <Flag ok={t.config.azienda} label="Dati azienda" /><Flag ok={t.config.logo} label="Logo" /><Flag ok={t.config.listino} label="Listino" />
      </td>
    </tr>
  );
}

export default function OwnerUsage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showTest, setShowTest] = useState(false);
  useEffect(() => { http.get("/platform/tenants/usage").then((r) => setData(r.data)).catch((e) => setErr(apiErr(e))); }, []);
  if (err) return <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-lg px-4 py-3 text-sm" data-testid="usage-error">{err}</div>;
  if (!data) return <Loading />;
  const rows = data.tenants.filter((t) => showTest || !t.is_test);
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="owner-usage">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Utilizzo per azienda</h1>
        <p className="text-sm text-slate-400 mt-1">Distingue l'iscrizione dall'uso effettivo. Metriche aggregate, nessun contenuto di clienti o documenti.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-5" data-testid="usage-summary">
        <div className="bg-white/5 border border-white/10 rounded-xl p-3"><div className="text-[11px] text-slate-400">Aziende reali</div><div className="text-xl font-bold" data-testid="usage-total-real">{s.tenants_real}</div></div>
        {Object.entries(STAGE).map(([k, [label, cls]]) => (
          <div key={k} className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="text-[11px] text-slate-400">{label}</div>
            <div className="text-xl font-bold" data-testid={`usage-stage-${k}`}>{s.by_stage[k] ?? 0}</div>
            <div className={`mt-1 text-[10px] leading-snug text-slate-500`}>{data.criteria[k]}</div>
          </div>
        ))}
      </div>
      <div className="flex items-start gap-2 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-xl p-3" data-testid="usage-history-note">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-300" /><span>{data.history_note}</span>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400">
        <input type="checkbox" data-testid="usage-show-test" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} /> Mostra anche aziende di test ({s.tenants_test})
      </label>
      <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-2xl p-4">
        <table className="w-full text-left text-sm min-w-[980px]" data-testid="usage-table">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="pb-2 pr-3">Azienda</th><th className="pb-2 pr-3">Stato / abbonamento</th><th className="pb-2 pr-3">Ultimo accesso</th><th className="pb-2 pr-3">Dati attuali</th><th className="pb-2 pr-3">PDF generati</th><th className="pb-2 pr-3">Ultima attività</th><th className="pb-2">Configurazione</th></tr>
          </thead>
          <tbody>{rows.map((t) => <Row key={t.tenant_id} t={t} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
